import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
  withXhr,
} from '@angular/common/http';
import {
  type ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
  withRouterConfig,
} from '@angular/router';
import { KeycloakConfig } from '@core/auth/keycloak';
import { initilizeTheme } from '@core/environment/theme.initializer';
import type { AppConfig } from '@core/environment/tokens';
import { LoadingHttpInterceptor } from '@core/interceptors/loading.interceptor';
import { AppNotificationService } from '@core/notification/app-notification.service';
import { FINO_VERSION } from '@fino-ui/common';
import { NotificationService } from '@fino-ui/notification';
import { ThemingService } from '@fino-ui/theming';
import {
  FINO_TRANSLATION_CONFIG,
  provideTranslationModule,
} from '@fino-ui/translation';
import { createErrorHandler } from '@sentry/angular';
import { Version } from '@version';
import { provideAngularSvgIcon } from 'angular-svg-icon';
import { provideHighcharts } from 'highcharts-angular';
import {
  createInterceptorCondition,
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  type IncludeBearerTokenCondition,
  includeBearerTokenInterceptor,
  provideKeycloak,
} from 'keycloak-angular';
import { NgxLoadingModule, ngxLoadingAnimationTypes } from 'ngx-loading';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { ToastrModule } from 'ngx-toastr';
import {
  provideNgxWebstorage,
  withLocalStorage,
  withNgxWebstorageConfig,
  withSessionStorage,
} from 'ngx-webstorage';
import { routes } from './app.routes';

const urlConditionForAccessTokenInclude =
  createInterceptorCondition<IncludeBearerTokenCondition>({
    urlPattern: /api.*?$/i,
    bearerPrefix: 'Bearer',
  });

export const appConfig = (config: AppConfig): ApplicationConfig => ({
  providers: [
    {
      provide: FINO_VERSION,
      useValue: Version,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoadingHttpInterceptor,
      multi: true,
    },
    {
      provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
      useValue: [urlConditionForAccessTokenInclude],
    },
    {
      provide: ErrorHandler,
      useValue: createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: NotificationService,
      useClass: AppNotificationService,
    },
    {
      provide: FINO_TRANSLATION_CONFIG,
      useValue: {
        availableLanguages: ['de'],
      },
    },
    importProvidersFrom(
      provideTranslationModule(),
      ToastrModule.forRoot(),
      NgxLoadingModule.forRoot({
        animationType: ngxLoadingAnimationTypes.circleSwish,
      }),
    ),
    provideAngularSvgIcon(),
    provideEnvironmentNgxMask(),
    provideHighcharts(),
    provideHttpClient(
      withXhr(),
      withInterceptors([includeBearerTokenInterceptor]),
    ),
    provideKeycloak(KeycloakConfig(config.keycloakHost, config.keycloakRealm)),
    provideNgxWebstorage(
      withNgxWebstorageConfig({
        caseSensitive: true,
      }),
      withLocalStorage(),
      withSessionStorage(),
    ),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withRouterConfig({
        // Reload the page if the same URL is requested
        onSameUrlNavigation: 'reload',
        // Bind route info to component inputs (e.g. route.params)
        paramsInheritanceStrategy: 'always',
      }),
    ),
    provideAppInitializer(() => initilizeTheme(inject(ThemingService))()),
    provideAppInitializer(() => {
      const notificationService = inject(NotificationService);
      notificationService.options = {
        ...notificationService.options,
        timeOut: 15000,
      };
    }),
  ],
});
